/// Port of mobile/lib/features/user/ui/delete_account_screen.dart.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { appRoutes } from '@/app/router/routes';
import { useSnackbarStore } from '@/app/ui/snackbar/snackbarStore';
import { useAuthStore } from '@/auth/handler/authStore';
import { useThemeColors } from '@/settings/handler/settingsStore';
import { PageChrome } from '@/ui/PageChrome';
import { PixelActionButton } from '@/ui/pixel/PixelActionButton';
import { PixelContainer } from '@/ui/pixel/PixelContainer';
import { maxUsernameLength } from '@/user/domain/username';
import * as userRepository from '@/user/data/userRepository';

export function DeleteAccountScreen() {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);
  const showError = useSnackbarStore((s) => s.error);
  const showSuccess = useSnackbarStore((s) => s.success);

  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const expected = user.username.trim();
  const canSubmit =
    !busy &&
    typed.trim().length > 0 &&
    typed.trim().toLowerCase() === expected.toLowerCase();

  const onDelete = async (): Promise<void> => {
    setError(null);
    if (typed.trim().toLowerCase() !== expected.toLowerCase()) {
      setError(t('profileDeleteAccountMismatch'));
      return;
    }
    setBusy(true);
    try {
      await userRepository.deleteAccount(typed.trim());
      showSuccess(t('profileDeleteAccountSuccess'));
      await signOut();
      navigate(appRoutes.home, { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : t('profileDeleteAccountFailed');
      const lower = message.toLowerCase();
      setError(
        lower.includes('match') || lower.includes('username')
          ? t('profileDeleteAccountMismatch')
          : message || t('profileDeleteAccountFailed'),
      );
      showError(t('profileDeleteAccountFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageChrome title={t('profileDeleteAccountTitle')} onBack={() => navigate(appRoutes.user)}>
      <div className="flex-1 overflow-y-auto px-6 pb-10 pt-5">
        <p
          className="leading-tight"
          style={{ fontSize: 16, color: colors.onSurface, opacity: 0.75 }}
        >
          {t('profileDeleteAccountHint')}
        </p>

        <p className="mt-5 leading-snug" style={{ fontSize: 18, color: colors.onSurface }}>
          {t('profileDeleteAccountConfirmLabel', { username: expected })}
        </p>

        <div className="mt-4 leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
          {t('profileDeleteAccountTypeUsername')}
        </div>
        <PixelContainer
          radius={3}
          pixelScale={2}
          padding="4px 12px"
          backgroundColor={colors.surfaceContainerHighest}
          borderColor={colors.onSurface}
          className="mt-2 w-full"
        >
          <input
            type="text"
            value={typed}
            disabled={busy}
            autoFocus
            autoComplete="off"
            autoCapitalize="none"
            autoCorrect="off"
            maxLength={maxUsernameLength}
            onChange={(e) => {
              setTyped(e.target.value);
              if (error) setError(null);
            }}
            className="w-full bg-transparent outline-none"
            style={{ fontSize: 22, color: colors.onSurface, padding: '6px 0' }}
          />
        </PixelContainer>

        {error && (
          <p className="mt-3 leading-tight" style={{ fontSize: 16, color: '#ef5350' }}>
            {error}
          </p>
        )}

        <PixelActionButton
          label={t('profileDeleteAccountSubmit')}
          busy={busy}
          className="mt-6"
          onClick={canSubmit ? () => void onDelete() : undefined}
        />
      </div>
    </PageChrome>
  );
}
